import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { saveAs } from "file-saver";
import { Spinner } from "react-bootstrap";
import NEW_API from "../../../Api/NewApi";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import Button from "../../Common/FormElements/Button/Button";
import CustomLineChart from "../../Common/CustomLineChart/CustomLineChart";
import TabsNavbar from "../../Common/TabsNavBar/TabsNavbar";
import StatCards, { deltaPct } from "./StatCards";
import ObjectivesModal from "./ObjectivesModal";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import { CATEGORY_META, formatEuro } from "./statisticsData";

const METRIC_TABS = ["Revenue", "Net margin", "Margin rate"];
const MONTH_KEYS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const METRIC_KEYS = {
  Revenue: "ca",
  "Net margin": "marge",
  "Margin rate": "txMarge",
};

/**
 * Page 1 — Commercial: period KPIs with YoY/objective deltas, objectives
 * entry, monthly per-category charts, cash indicators, objective coverage.
 * Reseller / business affiliate: the same page, order figures on THEIR orders
 * (server-scoped like the Commandes list).
 */
const CommercialStats = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { scope } = useSelector((state) => state.appCustomersReducer);
  // Bumped after the objectives modal saves — refetches the objective deltas.
  const [reload, setReload] = useState(0);
  // Null by default — the backend then defaults to year-start → today.
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [activeMetric, setActiveMetric] = useState(METRIC_TABS[0]);
  const [selectedCategories, setSelectedCategories] = useState([
    "spa",
    "swimSpa",
  ]);
  const [compareCategories, setCompareCategories] = useState(["spa"]);

  const abortRef = useRef(null);
  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    // Espace clients filter bar (admin: store / affiliate; affiliate: store).
    const scopeParams = {
      organizationId: scope.organizationId || undefined,
      businessAffiliateId: scope.businessAffiliateId || undefined,
    };
    const params = {
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : undefined,
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
      ...scopeParams,
    };
    (async () => {
      setLoading(true);
      try {
        const [commercial, monthlyRes] = await Promise.all([
          NEW_API.get("api/statistics/commercial", {
            params,
            signal: abortRef.current.signal,
          }),
          // Always by period (the end defaults to today, like /commercial).
          NEW_API.get("api/statistics/monthly", {
            params: {
              ...params,
              endDate: params.endDate ?? moment().format("YYYY-MM-DD"),
            },
            signal: abortRef.current.signal,
          }),
        ]);
        setStats(commercial.data?.data);
        setMonthly(monthlyRes.data?.data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
    return () => abortRef.current?.abort();
  }, [startDate, endDate, reload, scope.organizationId, scope.businessAffiliateId]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      // Espace clients filter bar, like the on-screen figures.
      const scopeParams = {
        organizationId: scope.organizationId || undefined,
        businessAffiliateId: scope.businessAffiliateId || undefined,
      };
      const response = await NEW_API.post(
        "api/statistics/commercial-pdf",
        {
          data: {
            startDate: startDate
              ? moment(startDate).format("YYYY-MM-DD")
              : undefined,
            endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : undefined,
          },
        },
        { params: scopeParams, responseType: "blob" },
      );
      saveAs(
        new Blob([response.data], { type: "application/pdf" }),
        `commercial_${moment(endDate ?? undefined).format("YYYY-MM-DD")}.pdf`,
      );
    } catch (error) {
      console.log(error);
    } finally {
      setExporting(false);
    }
  };

  const current = stats?.ca?.current;
  const previous = stats?.ca?.previous;

  // The 3 parts of the Commercial section: total + per-category cards.
  const bucketCards = (bucket, keys) => [
    { label: "Total", value: formatEuro(bucket?.total), bgColor: "#345d68" },
    ...keys.map((key) => {
      const meta = CATEGORY_META.find((c) => c.key === key);
      return {
        label: meta.label,
        value: formatEuro(bucket?.byCategory?.[key]),
        bgColor: meta.color,
      };
    }),
  ];
  const STOCK_KEYS = ["spa", "swimSpa", "sauna"];
  const CA_KEYS = [
    "spa",
    "swimSpa",
    "sauna",
    "sparePart",
    "accessory",
    "marketing",
  ];

  // % cards on CA Comptable: green when positive, red when negative.
  const pctCard = (label, pct) => ({
    label,
    value: pct === null ? "—" : `${pct >= 0 ? "+" : ""}${pct}%`,
    bgColor: pct === null ? "#878a8b" : pct >= 0 ? "#1f7a5c" : "#a4443f",
  });
  // Objective is day-prorated server-side over the selected period (total only).
  const caPctCards = [
    pctCard(
      "Percentage year N-1",
      deltaPct(current?.caComptable?.total, previous?.caComptable?.total),
    ),
    pctCard(
      "Percentage objective",
      deltaPct(current?.caComptable?.total, stats?.objective?.totalValue),
    ),
  ];

  const toggleIn = (list, setList, key, max) =>
    setList((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (max && prev.length >= max) return prev;
      return [...prev, key];
    });

  const metricKey = METRIC_KEYS[activeMetric];
  const isRate = metricKey === "txMarge";
  // Charts cover the period's months; "previous" = the same months a year earlier.
  const months = monthly?.months ?? [];
  const multiYear = new Set(months.map((month) => month.slice(0, 4))).size > 1;
  const monthLabels = months.map(
    (month) =>
      `${t(MONTH_KEYS[Number(month.slice(5, 7)) - 1])}${
        multiYear ? ` ${month.slice(2, 4)}` : ""
      }`,
  );
  const zeros = Array(months.length).fill(0);
  const chartYear =
    Number(monthly?.period?.endDate?.slice(0, 4)) || moment().year();
  const prevYear = chartYear - 1;

  // Multi-category chart: one series per selected category, the period.
  const categoryChartData = {};
  const categoryChartColors = [];
  selectedCategories.forEach((key) => {
    const meta = CATEGORY_META.find((c) => c.key === key);
    categoryChartData[t(`${meta.label}`)] =
      monthly?.[metricKey]?.[key]?.current ?? zeros;
    categoryChartColors.push(meta.color);
  });

  // Comparison chart: each chosen category (max 2) vs the same period N-1.
  const compareChartData = {};
  const compareChartColors = [];
  compareCategories.forEach((key, index) => {
    const meta = CATEGORY_META.find((c) => c.key === key);
    compareChartData[`${t(`${meta.label}`)} ${chartYear}`] =
      monthly?.[metricKey]?.[key]?.current ?? zeros;
    compareChartData[`${t(`${meta.label}`)} ${prevYear}`] =
      monthly?.[metricKey]?.[key]?.previous ?? zeros;
    compareChartColors.push(meta.color, index === 0 ? "#c4c4c4" : "#8a8a8a");
  });

  const chips = (selected, onToggle) => (
    <div className="d-flex flex-wrap gap-2 mb-2">
      {CATEGORY_META.map(({ key, label, color }) => (
        <button
          key={key}
          className={`px-3 py-1 br30 f13 sBold border-2 ${
            selected.includes(key) ? "text-white border-0" : "bg-white"
          }`}
          style={
            selected.includes(key)
              ? { backgroundColor: color }
              : { borderColor: color, color }
          }
          onClick={() => onToggle(key)}
        >
          {t(`${label}`)}
        </button>
      ))}
    </div>
  );

  return (
    <div className="position-relative mt-5">
      {loading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 z-9999 d-flex align-items-center justify-content-center">
          <Spinner />
        </div>
      )}
      <div className="d-flex align-items-end flex-wrap gap-3 mb-2">
        <h3 className="text-blue bold f18 quickSand mb-0 me-auto">
          {t("Commercial")}
        </h3>
        <div className="max-w-200px w-100">
          <CalendarInput
            name="startDate"
            placeholder={t("From")}
            value={startDate}
            withMinDate={false}
            minDate={false}
            callback={(e) => e.target.value && setStartDate(e.target.value)}
          />
        </div>
        <div className="max-w-200px w-100">
          <CalendarInput
            name="endDate"
            placeholder={t("To")}
            value={endDate}
            withMinDate={false}
            minDate={false}
            callback={(e) => e.target.value && setEndDate(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          Icon={() => <i className="bi bi-bullseye fa-lg" />}
          onClick={() =>
            dispatch(
              openCenteredModal(
                <ObjectivesModal onSaved={() => setReload((r) => r + 1)} />,
              ),
            )
          }
        >
          {t("Objectives")}
        </Button>
        <Button
          variant="outline"
          loading={exporting}
          Icon={() => <i className="bi bi-download fa-lg" />}
          onClick={exportPdf}
        >
          {t("Export PDF")}
        </Button>
      </div>

      <h4 className="f16 sBold text-ultraBlack mt-3">
        {t("Commercial stock value")}
      </h4>
      <StatCards
        // featuredFirst
        cards={bucketCards(stats?.stockCommercial, STOCK_KEYS)}
      />
      <h4 className="f16 sBold text-ultraBlack mt-3">
        {t("Commercial stock value on supply")}
      </h4>
      <StatCards
        // featuredFirst
        cards={bucketCards(stats?.stockCommercialAppro, STOCK_KEYS)}
      />
      <h4 className="f16 sBold text-ultraBlack mt-3">
        {t("Accounting revenue")}
      </h4>
      <StatCards
        featuredFirst
        cards={bucketCards(current?.caComptable, CA_KEYS)}
      />
      <StatCards cards={caPctCards} />

      <div className="mt-4">
        <TabsNavbar
          tabs={METRIC_TABS}
          activeTab={activeMetric}
          setActiveTab={setActiveMetric}
        />
        <div className="mt-3">
          {chips(selectedCategories, (key) =>
            toggleIn(selectedCategories, setSelectedCategories, key),
          )}
          <CustomLineChart
            years={Object.keys(categoryChartData)}
            colors={categoryChartColors}
            data={categoryChartData}
            labels={monthLabels}
            unity={isRate ? "%" : "k"}
            hasUnity
          />
        </div>
        <div className="mt-4">
          <h4 className="f16 sBold text-ultraBlack">
            {t("Category vs previous year")}
          </h4>
          {chips(compareCategories, (key) =>
            toggleIn(compareCategories, setCompareCategories, key, 2),
          )}
          <CustomLineChart
            years={Object.keys(compareChartData)}
            colors={compareChartColors}
            data={compareChartData}
            labels={monthLabels}
            unity={isRate ? "%" : "k"}
            hasUnity
          />
        </div>
      </div>

      {/* Slides 10–11 — remaining Commercial blocks */}
      <h4 className="f16 sBold text-ultraBlack mt-4">
        {t("Commercial revenue")}
      </h4>
      <StatCards
        featuredFirst
        cards={bucketCards(current?.caCommercial, CA_KEYS)}
      />
      <StatCards
        cards={[
          pctCard(
            "Percentage year N-1",
            deltaPct(
              current?.caCommercial?.total,
              previous?.caCommercial?.total,
            ),
          ),
        ]}
      />
      <h4 className="f16 sBold text-ultraBlack mt-3">{t("Order revenue")}</h4>
      <StatCards
        featuredFirst
        cards={bucketCards(current?.caCommande, CA_KEYS)}
      />
      <StatCards
        cards={[
          pctCard(
            "Percentage year N-1",
            deltaPct(current?.caCommande?.total, previous?.caCommande?.total),
          ),
        ]}
      />
      <StatCards
        cards={[
          {
            label: "Outstanding payments",
            value: formatEuro(stats?.cash?.outstandingPayments),
            bgColor: "#cabaab",
          },
          {
            label: "Upcoming deposits",
            value: formatEuro(stats?.cash?.upcomingDeposits),
            bgColor: "#94adb2",
          },
        ]}
      />
      {/* (Valeur Stock Commerciale + appro arriving in period) − objectif */}
      <h4 className="f16 sBold text-ultraBlack mt-3">{t("Supply")}</h4>
      <StatCards
        featuredFirst
        cards={["total", "spa", "swimSpa", "sauna", "accessory"].map((key) => {
          const meta = CATEGORY_META.find((c) => c.key === key);
          const value = stats?.objectiveCoverage?.[key] ?? 0;
          return {
            label: meta
              ? `Evaluation Stock/Appro vs CA M ${meta.label}`
              : "Evaluation Stock/Appro vs CA M",
            value: formatEuro(value),
            bgColor: value >= 0 ? "#1f7a5c" : "#a4443f",
          };
        })}
      />
    </div>
  );
};

export default CommercialStats;
